import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Alert, Box, Checkbox, Chip, Divider, FormControl, FormControlLabel, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, Link, List, ListItem, ListItemIcon, ListItemText, MenuItem, Select, TextField, Typography } from '@mui/material';
import { green, grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { Prompt } from 'react-router';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { DataAPIContext, DELETE, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import NavigateBackBar from '../components/NavigateBackBar';
import PageMeta from '../components/PageMeta';
import RecordDetails, { RecordDetailsContext } from '../components/RecordDetails';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { TOUR_TAG_PICKER_PAGING } from '../components/tourTagPickerPaging';
import { language_iso_codes } from '../notifications/utils';
import sharedContext from '../sharedContext';
import { ContextStore } from '../store/contextStore';
import { copyTextToClipboard, fromServerErrors, muiErrorAttrs } from '../utils';
import { useIsWidthUp } from '../utils';
import { validateUser } from '../utils/validators';
import { roleChangeError } from './roleAccessRefusal';
import UploadAvatarImage from './UploadAvatarImage';
import { ConfirmationDialogText } from './utils';
import UserContactList from './UserContactList';

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        border: `1px solid ${grey[200]}`,
        borderRadius: '5px',
        margin: theme.spacing(2),
        backgroundColor: '#FCFCFE',
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    userProfile: {
        height: 'calc(100vh - 19rem) !important',
        overflowY: 'scroll',
        padding: theme.spacing(2),
        flexGrow: 1,
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    buttonPanel: {},
    formControl: {
        minWidth: 120,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        margin: 2,
    },
    verifiedUserIcon: {
        color: green[600],
    },
}));

const getComparableState = (user) => {
    const a = {
        ...user,
    };
    delete a._changed;
    delete a._changed_first_name;
    delete a._changed_last_name;
    delete a._changed_phone;
    delete a._changed_email;
    delete a._changed_is_tour_operator_staff;
    delete a._changed_has_admin_access;
    delete a._changed_roles;
    delete a._changed_mark_thread_as_read_on_any_staff_reply;
    delete a._changed_allow_email_nf;
    delete a._changed_allow_sms_nf;
    delete a._changed_allow_whatsapp_nf;
    delete a._changed_allow_push_nf;
    delete a._changed_exclude_from_chat;
    delete a._changed_exclude_from_group_chat;
    delete a._changed_block_incoming_emails;
    delete a._changed_block_incoming_sms;
    delete a._changed_block_incoming_whatsapp;
    delete a._changed_contacts;
    delete a._changed_freeze_email;
    delete a._changed_freeze_phone;
    delete a._deleted_contact_ids;
    return JSON.stringify(a);
};

const UserProfileEdit = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    const location = useLocation();
    const classes = useStyles();
    const contextStoreData = React.useContext(ContextStore);

    const { roles, roleMap, hasPerm, isFranchise } = contextStoreData;
    const setGlobalUserData = contextStoreData.setUser;

    const [syncActive, setSyncActive] = React.useState(false);
    const [user, setUser] = React.useState(props.data);
    const [serverError, setServerError] = React.useState(null);
    const [serverValidationErrors, setServerValidationErrors] = React.useState({});
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');

    const [originalState, setOriginalState] = React.useState(getComparableState(props.data))
    const currentState = getComparableState(user);
    const isChanged = originalState !== currentState;

    const { canSave, errors } = validateUser(user);

    const saveDisabled = syncActive || !canSave || !user._changed;
    const isExistingImage = user.avatar && user.avatar.p && user.avatar.s;
    const hasWritePerm = hasPerm('user', 'write')

    const onLicenseeChange = (id) => {
        setUser((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    const langmap = {};
    language_iso_codes.forEach((lng) => {
        langmap[lng.code] = lng;
    });

    const handleSave = (ev) => {
        setSyncActive(true);
        (async () => {
            try {
                // Clean internal tracking fields before sending to server
                const { _deleted_contact_ids, ...userData } = user;
                // keepSessionOn403: WP-K refuses a role grant or removal beyond the caller's own access (403);
                // that is an answer, not an expired session.
                const res = await dataAPI(UPDATE, props.resource, { id: userData.id, data: userData, upload: userData._upload, keepSessionOn403: true });
                console.log('update', res);
                if (res.statusCode === 200 || res.statusCode === 201) {
                    enqueueSnackbar('User saved', { variant: 'success' });
                    setServerValidationErrors({});
                    const responseData = { ...res.data };
                    // Filter out locally deleted contacts from server response
                    // (in case the backend doesn't handle nested contact deletion via PUT)
                    if (_deleted_contact_ids?.length > 0 && responseData.contacts) {
                        const deletedSet = new Set(_deleted_contact_ids);
                        responseData.contacts = responseData.contacts.filter(c => !deletedSet.has(c.id));
                    }
                    setUser(() => ({ ...responseData, _changed: false }));
                    setOriginalState(getComparableState(responseData))
                    //Update global User object if editing self user data
                    if (contextStoreData.user.id === user.id) {
                        let existing_bs_shared_context = sharedContext.get('bootstrap');
                        sharedContext.set('bootstrap', {
                            ...existing_bs_shared_context,
                            user: responseData,
                        });
                        setGlobalUserData(responseData);
                    }
                } else if (res.statusCode === 406) {
                    const fieldErrors = fromServerErrors(res.data);
                    setServerValidationErrors(fieldErrors);
                    const topMessage = typeof res.data?.message === 'string' && res.data.message.trim() ? res.data.message.trim() : null;
                    const inlineFieldMessages = Object.values(fieldErrors)
                        .flat()
                        .filter((m) => typeof m === 'string' && m.trim());
                    enqueueSnackbar(
                        inlineFieldMessages.length ? inlineFieldMessages.join(' ') : topMessage || 'Please check for errors',
                        { variant: 'warning' }
                    );
                } else if (res.statusCode === 403) {
                    // The edits stay in the form (still unsaved).
                    enqueueSnackbar(roleChangeError(res, 'Error saving user'), { variant: 'error' });
                } else {
                    const errorMessage = res.data?.message || 'Error saving user';
                    enqueueSnackbar(errorMessage, { variant: 'error' });
                }
            } catch (e) {
                enqueueSnackbar('Error saving user', { variant: 'error' });
                console.log('error saving user', e);
            } finally {
                setSyncActive(false);
            }
        })();
    };
    const doDelete = () => {
        setServerError(null);
        setSyncActive(true);
        (async () => {
            try {
                const res = await dataAPI(DELETE, props.resource, { id: user.id });
                if (res.statusCode === 200 || res.statusCode === 204) {
                    enqueueSnackbar('User deleted', { variant: 'default' });
                    history.push(`/user`, { deleted: user.id });
                } else if (res.statusCode === 406) {
                    console.log('validation errors', res.data.message);
                    enqueueSnackbar('Error deleting user', { variant: 'error' });
                    setServerError(res.data.message);
                } else if (res.data?.message) {
                    enqueueSnackbar(res.data.message, { variant: 'error' });
                }
            } catch (e) {
                enqueueSnackbar('Error deleting user', { variant: 'error' });
            } finally {
                setSyncActive(false);
            }
        })();
    };

    const handleDelete = (ev) => {
        setConfirmationDialogAction('delete_user');
    };

    // CP-8423: back to the View User page, same target as the NavigateBackBar
    // arrow; the <Prompt> still guards unsaved changes.
    const handleCancel = () => {
        history.push(`/user/${user.id}${location.search || ''}`);
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_user':
                doDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const cleanServerValidationError = (fid) => {
        if (!serverValidationErrors || !serverValidationErrors[fid]) {
            return;
        }
        delete serverValidationErrors[fid];
    };

    const onUpdateContacts = (newUser) => {
        console.log('onUpdateContacts', newUser.contacts);
        setServerValidationErrors((prev) => {
            if (!prev?.email && !prev?.phone) {
                return prev;
            }
            const next = { ...prev };
            delete next.email;
            delete next.phone;
            return next;
        });
        const fid = `_changed_contacts`;
        setUser((prevState) => {
            const newState = {
                ...prevState,
                contacts: newUser.contacts,
                _changed: true, [fid]: true
            };
            if (newUser.email !== prevState.email) {
                newState.email = newUser.email;
                newState._changed_email = true;
            }
            if (newUser.phone !== prevState.phone) {
                newState.phone = newUser.phone;
                newState._changed_phone = true;
            }
            if (newUser.freeze_email !== prevState.freeze_email) {
                newState.freeze_email = newUser.freeze_email;
                newState._changed_freeze_email = true;
            }
            if (newUser.freeze_phone !== prevState.freeze_phone) {
                newState.freeze_phone = newUser.freeze_phone;
                newState._changed_freeze_phone = true;
            }
            // Track deleted contact IDs so we can filter them from server response
            if (newUser._deleted_contact_ids) {
                newState._deleted_contact_ids = newUser._deleted_contact_ids;
            }
            return newState;
        });
    };

    const handleOnTextChange = (ev) => {
        const fid = `_changed_${ev.target.name}`;
        cleanServerValidationError(ev.target.name);
        setUser((prevState) => ({ ...prevState, [ev.target.name]: ev.target.value, _changed: true, [fid]: true }));
    };

    const onContextTextChange = (ev) => {
        setUser((prevState) => ({
            ...prevState,
            context: {
                ...(prevState.context || {}),
                [ev.target.name]: ev.target.value,
            },
            _changed: true,
            _changed_context: true,
        }));
    };

    const handleCheckboxChange = (ev) => {
        const fid = `_changed_${ev.target.name}`;
        cleanServerValidationError(ev.target.name);
        setUser((prevState) => ({ ...prevState, [ev.target.name]: ev.target.checked, _changed: true, [fid]: true }));
    };

    const handleUserRolesChange = (ev) => {
        const newRoleIds = ev.target.value.constructor === Array ? [...ev.target.value] : [+ev.target.value];
        cleanServerValidationError('roles');
        setUser((prevState) => ({ ...prevState, roles: newRoleIds, _changed: true, _changed_roles: true }));
    };

    const onLanguageChange = (ev, lang, reason) => {
        if (!lang) {
            setUser((prevState) => ({ ...prevState, lang: null, _changed: true }));
            return;
        }
        setUser((prevState) => ({ ...prevState, lang: lang?.id || prevState.lang, _changed: true }));
    };

    const handleLinkClick = (ev) => {
        ev.preventDefault();
        const href = ev.target.getAttribute('href');
        history.push(href);
    };

    const copyToClipboard = (text, label) => {
        copyTextToClipboard(text);
        enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onDrop = React.useCallback((acceptedFiles) => {
        console.log('onDrop', acceptedFiles, props.day);
        setUser({
            ...user,
            avatar: acceptedFiles[0],
            _upload: true,
            _changed: true,
        });
    });
    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onDrop,
        multiple: false,
    });

    const handleImageDelete = () => {
        setUser((prevState) => ({ ...prevState, _changed: true, avatar: null }));
    };

    const onAccessTagsChange = (ev, atags, reason) => {
        setUser((prevState) => ({ ...prevState, atag: atags, _changed: true }));
    };

    // Account-level tags (CP-8095): facts about the PERSON ("Part-Time Staff"),
    // independent of the tags on their individual bookings. Notification rules
    // target them with "User account has one of the tags".
    const onAccountTagsChange = (ev, tags, reason) => {
        setUser((prevState) => ({ ...prevState, tags: tags, _changed: true }));
    };

    if (!hasPerm('user', 'read')) {
        <AccessDeniedView />;
    }
    const emailServerAttrs = muiErrorAttrs('email', serverValidationErrors);

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(user.tour_operator)}>
        <div className={classes.root}>
            <PageMeta title={`Edit User - ${user.first_name || user.last_name ? user.first_name + ' ' + user.last_name : user.display_name}`} />
            <Prompt when={!!user._changed && hasPerm('user', 'write')} message="Are you sure you want to leave without saving changes?" />
            <FixedLinearProgress visibility={syncActive} />

            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <NavigateBackBar label={user.display_name} url={`/user/${user.id}${location.search || ''}`} labelDescription={'User'} />
            <Divider />
            <div className={classes.userProfile}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Edit User Info</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <input {...dz.getInputProps()} />
                        <UploadAvatarImage disabled={!hasWritePerm} borderRadius={'50%'} onClick={dz.open} onDelete={handleImageDelete} isUpload={!isExistingImage} image={user.avatar} customPlaceHolderImage={true} />{' '}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField {...errors.first_name} disabled={!hasWritePerm} variant="filled" size="small" name="first_name" label="First Name" value={user.first_name || ''} fullWidth InputLabelProps={{ shrink: true }} onChange={handleOnTextChange} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField {...errors.last_name} disabled={!hasWritePerm} variant="filled" size="small" name="last_name" label="Last Name" value={user.last_name || ''} fullWidth InputLabelProps={{ shrink: true }} onChange={handleOnTextChange} />
                    </Grid>
                    <Grid item xs={12}>
                        {/* CP-7706: primary email/phone are read-only here (greyed like the
                            other uneditable fields). User.email/phone mirror the primary
                            contact, so every edit goes through Contacts. The copy buttons stay
                            clickable: MUI's disabled input does not disable its adornments. */}
                        <TextField
                            {...errors.email}
                            {...emailServerAttrs}
                            disabled
                            variant="filled"
                            size="small"
                            name="email"
                            label="Email"
                            value={user.email || ''}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <>
                                        {user.is_email_verified && !user._changed_email ? (
                                            <InputAdornment position="end">
                                                <VerifiedUserIcon title="Email address verified" className={classes.verifiedUserIcon} />
                                            </InputAdornment>
                                        ) : null}
                                        {user.email ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={(ev) => copyToClipboard(user.email, 'Email')}>
                                                    <FileCopyOutlinedIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null}
                                    </>
                                ),
                            }}
                            helperText={emailServerAttrs.helperText || errors.email?.helperText || 'Edit in Contacts below'}
                        />
                    </Grid>
                    {user.phone ? (
                        <Grid item xs={12}>
                            <TextField
                                disabled
                                variant="filled"
                                size="small"
                                name="phone"
                                label={
                                    user.sms_is_undeliverable ? (
                                        <div title={'Number is unreachable'} style={{ display: 'flex', alignItems: 'center' }}>
                                            Phone&nbsp;<ErrorOutlineIcon style={{ color: '#999999', height: '18px', width: '18px' }} />
                                        </div>
                                    ) : (
                                        'Phone'
                                    )
                                }
                                value={user.phone}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={(ev) => copyToClipboard(user.phone, 'Phone')}>
                                                <FileCopyOutlinedIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                helperText="Edit in Contacts below"
                            />
                        </Grid>
                    ) : null}
                    <Grid item xs={12}>
                        <AccessTagsDropdown disabled={!hasWritePerm} value={user.atag} onChange={onAccessTagsChange} />
                    </Grid>
                    <Grid item xs={12}>
                        <RemoteAutocomplete
                            filterSelected
                            disabled={!hasWritePerm}
                            resource="tour_tag"
                            label="Tags"
                            multiple
                            variant="filled"
                            size="small"
                            fullWidth
                            urlSuffix={null}
                            onChange={onAccountTagsChange}
                            {...TOUR_TAG_PICKER_PAGING}
                            value={user.tags || []}
                            getOptionLabel={(opt) => {
                                const categoryCode = typeof opt.category === 'string' ? opt.category : (opt.category?.code || '');
                                return categoryCode ? `${categoryCode}: ${opt.name}` : `${opt.name}`;
                            }}
                            filterOptions={(optlist) => optlist}
                            mapResults={(optlist, res) => optlist.map((x) => ({ id: x.id, name: x.name, category: x.category.code }))}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <RemoteAutocomplete disabled={!hasWritePerm} getOptionLabel={(opt) => opt.name ?? opt} resource="user" urlSuffix={'language_list'} label="Language" variant="filled" size="small" fullWidth onChange={onLanguageChange} value={user.lang} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField disabled={!hasWritePerm} multiline minRows={2} variant="filled" size="small" name="bio" label="Bio" value={user.bio || ''} fullWidth InputLabelProps={{ shrink: true }} onChange={handleOnTextChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} container justify="flex-end" alignItems="baseline">
                        {/* <FormControlLabel
                        control={<Checkbox 
                            color="primary" 
                            name="is_email_verified" 
                            checked={user.is_email_verified} 
                            onChange={handleCheckboxChange}/>} 
                        label="Email verified"
                    /> */}
                    </Grid>
                    { typeof user.contacts !== 'undefined' && (
                        <>
                        <Grid item xs={12}>
                            <Typography variant="h6">Contacts</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <UserContactList user={user} contacts={user.contacts} onUpdate={onUpdateContacts} readOnly={false} />
                        </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <Typography variant="h6">Notification Permissions</Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon>
                                    <Checkbox disabled={!hasWritePerm} color="primary" name="allow_email_nf" checked={user.allow_email_nf} onChange={handleCheckboxChange} />
                                </ListItemIcon>
                                <ListItemText>Send Email Notifications</ListItemText>
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Checkbox disabled={!hasWritePerm} color="primary" name="allow_sms_nf" checked={user.allow_sms_nf} onChange={handleCheckboxChange} />
                                </ListItemIcon>
                                <ListItemText>Send SMS Notifications</ListItemText>
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Checkbox disabled={!hasWritePerm} color="primary" name="allow_whatsapp_nf" checked={user.allow_whatsapp_nf} onChange={handleCheckboxChange} />
                                </ListItemIcon>
                                <ListItemText>Send WhatsApp Notifications</ListItemText>
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Checkbox disabled={!hasWritePerm} color="primary" name="allow_push_nf" checked={user.allow_push_nf} onChange={handleCheckboxChange} />
                                </ListItemIcon>
                                <ListItemText>Send Push Notifications</ListItemText>
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6">Staff Status</Typography>
                    </Grid>
                    {user.staff_teardown_in_progress ? (
                        <Grid item xs={12}>
                            <Alert severity="warning">
                                Staff removal is in progress — access groups and admin access have been revoked and the
                                user is being removed from all chats. They cannot be made a staff user or added to
                                access groups until this completes.
                            </Alert>
                        </Grid>
                    ) : null}
                    <Grid item xs={12} sm={4}>
                        <FormControlLabel disabled={!hasWritePerm || user.staff_teardown_in_progress} control={<Checkbox color="primary" name="is_tour_operator_staff" checked={user.is_tour_operator_staff} onChange={handleCheckboxChange} />} label="Is a Staff User" />
                    </Grid>
                    {user.is_tour_operator_staff ? (
                        <Grid item xs={12} sm={8} container alignItems="center">
                            <FormControlLabel disabled={!hasWritePerm} control={<Checkbox color="primary" name="has_admin_access" checked={user.has_admin_access} onChange={handleCheckboxChange} />} label="Admin Access" />(
                            <Link href={`/user/${user.id}/password-reset`} onClick={handleLinkClick}>
                                Reset Password
                            </Link>
                            )
                        </Grid>
                    ) : null}
                    {user.is_tour_operator_staff ? (
                        <Grid item xs={12}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                name="chat_alias"
                                label="Chat name shown to guests"
                                value={user.chat_alias || ''}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ maxLength: 150 }}
                                onChange={handleOnTextChange}
                                helperText="Optional. Guests see this name instead of the staff member's real name in chat and push notifications, and don't see their email or phone. Other staff still see the real name in chat."
                                {...muiErrorAttrs('chat_alias', serverValidationErrors)}
                            />
                        </Grid>
                    ) : null}

                    {user.is_tour_operator_staff ? (
                        <React.Fragment>
                            <Grid item xs={12}>
                                <FormControl disabled={!hasWritePerm} variant="filled" size="small" fullWidth className={classes.formControl} error={!!serverValidationErrors['roles']}>
                                    <InputLabel htmlFor="user-roles-select-element">Roles</InputLabel>
                                    <Select
                                        multiple
                                        value={user.roles}
                                        onChange={handleUserRolesChange}
                                        {...errors.roles}
                                        {...muiErrorAttrs('roles', serverValidationErrors, false)}
                                        inputProps={{ name: 'userRoles', id: 'user-roles-select-element' }}
                                        style={{ textTransform: 'capitalize' }}
                                        renderValue={(selected) => (
                                            <div className={classes.chips}>
                                                {selected.map((id) => {
                                                    const role = roleMap[id];
                                                    return (
                                                        <Chip
                                                            key={id}
                                                            label={role ? role.name : id}
                                                            color="primary"
                                                            className={classes.chip}
                                                            onMouseDown={(ev) => ev.stopPropagation()}
                                                            onDelete={
                                                                hasWritePerm
                                                                    ? () => handleUserRolesChange({ target: { value: selected.filter((rid) => rid !== id) } })
                                                                    : undefined
                                                            }
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                    >
                                        {roles.map((r) => (
                                            <MenuItem key={r.id} value={r.id} style={{ textTransform: 'capitalize' }}>
                                                {r.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {
                                        // HelpText doesn't work with the Select control (many complaints online about this), this is a workaround
                                    }
                                    <FormHelperText error>{serverValidationErrors['roles']}</FormHelperText>
                                </FormControl>
                            </Grid>
                        </React.Fragment>
                    ) : null}
                    <Grid item xs={12}>
                        <Typography variant="h6">Authentication</Typography>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <FormControlLabel disabled={!hasWritePerm} control={<Checkbox color="primary" name="bypass_auth" checked={user.bypass_auth} onChange={handleCheckboxChange} />} label="Bypass magic link authentication" />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Tipping</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="PayPal" fullWidth value={user.context?.tip_paypal} name="tip_paypal" onChange={onContextTextChange} InputLabelProps={{ shrink: true }} helperText="PayPal ID" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Venmo" fullWidth value={user.context?.tip_venmo} name="tip_venmo" onChange={onContextTextChange} InputLabelProps={{ shrink: true }} helperText="Venmo ID" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="TipDirect" fullWidth value={user.context?.tip_tipdirect} name="tip_tipdirect" onChange={onContextTextChange} InputLabelProps={{ shrink: true }} helperText="TipDirect ID" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Other" fullWidth value={user.context?.tip_link} name="tip_link" onChange={onContextTextChange} InputLabelProps={{ shrink: true }} helperText="Link to the user in any other payment system" />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Bank name" fullWidth value={user.context?.tip_bank_name} name="tip_bank_name" onChange={onContextTextChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Bank account" fullWidth value={user.context?.tip_bank_account} name="tip_bank_account" onChange={onContextTextChange} InputLabelProps={{ shrink: true }} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography variant="h6">Chat Status</Typography>
                    </Grid>
                    <Grid item xs={12}>
                    <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Airbnb User ID" fullWidth defaultValue={user.airbnb_user_id || ""} name="airbnb_user_id" onChange={handleOnTextChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    {user.is_guest &&
                    (<Grid item xs={12}>
                        <FormControlLabel disabled={!hasWritePerm} control={<Checkbox color="primary" name="exclude_from_chat" checked={user.exclude_from_chat} onChange={handleCheckboxChange} />} label="Don't create new chat conversations for this user" />
                    </Grid>
                    )}
                    {user.is_guest && 
                    (<Grid item xs={12}>
                        <FormControlLabel disabled={!hasWritePerm} control={<Checkbox color="primary" name="exclude_from_group_chat" checked={user.exclude_from_group_chat || user.exclude_from_chat} onChange={handleCheckboxChange} />} label="Exclude from group chat" />
                    </Grid>
                    )}
                    <Grid item xs={12}>
                        <FormControlLabel disabled={!hasWritePerm} control={<Checkbox color="primary" name="block_incoming_emails" checked={user.block_incoming_emails} onChange={handleCheckboxChange} />} label="Block incoming email messages from this user" />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel disabled={!hasWritePerm} control={<Checkbox color="primary" name="block_incoming_sms" checked={user.block_incoming_sms} onChange={handleCheckboxChange} />} label="Block incoming SMS messages from this user" />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel disabled={!hasWritePerm} control={<Checkbox color="primary" name="block_incoming_whatsapp" checked={user.block_incoming_whatsapp} onChange={handleCheckboxChange} />} label="Block incoming WhatsApp messages from this user" />
                    </Grid>
                </Grid>
            </div>

            {serverError ? (
                <Box p={1}>
                    <Alert severity="error">{serverError}</Alert>
                </Box>
            ) : null}

            <Box p={1} className={classes.buttonPanel}>
                <FormActionButtons
                    mode="edit"
                    onSubmit={handleSave}
                    submitDisabled={!hasWritePerm || saveDisabled || !isChanged}
                    onCancel={handleCancel}
                    cancelDisabled={syncActive}
                    onDelete={handleDelete}
                    deletePerm="user.delete"
                    deleteDisabled={syncActive}
                />
            </Box>
        </div>
        </FormLicenseeContext.Provider>
    );
};

const UserEdit = (props) => {
    const resource = 'user';
    const routeParams = useParams();
    const id = routeParams.id;
    const { hasPerm } = React.useContext(ContextStore);

    if (!hasPerm(resource, 'read')) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={id}>
            <RecordDetailsContext.Consumer>{(data) => <UserProfileEdit resource={resource} id={props.id} data={data} />}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default UserEdit;
